/** @format */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Chip,
    MenuItem,
    TextField,
    Table,
    TableBody,
    TableHead,
    TableRow,
    CircularProgress,
    Pagination,
    Alert,
    Stack,
} from "@mui/material";
import {
    SupportAgentOutlined,
    SearchOutlined,
    RefreshOutlined,
} from "@mui/icons-material";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { selectIsPlatformSuperAdmin } from "../../../redux/selectors/collectSelector";
import { fetchAdminSupportTickets } from "../../../redux/slices/collectSupportSlice";
import {
    IAdminSupportTicketRow,
    SupportTicketStatus,
} from "../../../utility/types/collectTypes";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectBadge,
    CollectHeroTitle,
    CollectHeroSubtitle,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
} from "../styles";
import {
    SUPPORT_CATEGORY_OPTIONS,
    SUPPORT_PRIORITY_OPTIONS,
    supportStatusChipSx,
    supportStatusLabel,
    supportTierChipSx,
    subscriptionTierChipSx,
    formatDateTime,
} from "../supportShared";

const PAGE_SIZE = 20;

const STATUS_FILTERS = [
    { value: "", label: "All statuses" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In progress" },
    { value: "waiting_on_user", label: "Waiting on user" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
    { value: "reopened", label: "Reopened" },
];

const TIER_FILTERS = [
    { value: "", label: "All tiers" },
    { value: "urgent", label: "Urgent" },
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
];

const CollectSupportQueue: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const isPlatformSuperAdmin = useSelector(selectIsPlatformSuperAdmin);
    const { adminTickets, adminPagination, isLoading, error } = useSelector(
        (s: RootState) => s.collectSupport,
    );

    const [page, setPage] = useState(1);
    const [status, setStatus] = useState("");
    const [category, setCategory] = useState("");
    const [priority, setPriority] = useState("");
    const [tier, setTier] = useState("");
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const load = useCallback(() => {
        dispatch(
            fetchAdminSupportTickets({
                status: (status || undefined) as SupportTicketStatus | undefined,
                category: (category || undefined) as any,
                priority: (priority || undefined) as any,
                tier: (tier || undefined) as any,
                search: search || undefined,
                page,
                limit: PAGE_SIZE,
            }),
        );
    }, [dispatch, status, category, priority, tier, search, page]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <CollectPageWrapper>
            <CollectHeroBanner>
                <CollectHeroInner>
                    <CollectBadge
                        icon={<SupportAgentOutlined sx={{ fontSize: 16 }} />}
                        label="Super Admin · Incident Queue"
                    />
                    <CollectHeroTitle>Support Incident Queue</CollectHeroTitle>
                    <CollectHeroSubtitle>
                        Every support ticket across the platform, ordered by
                        support priority so paid members are worked first.
                    </CollectHeroSubtitle>
                </CollectHeroInner>
            </CollectHeroBanner>

            <CollectContentArea>
                {!isPlatformSuperAdmin && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This area is restricted to platform super admins.
                    </Alert>
                )}

                <CollectSectionCard>
                    <CollectSectionHeader>
                        <Box>
                            <CollectSectionTitle>Tickets</CollectSectionTitle>
                            <CollectSectionSubtitle>
                                {adminPagination.total} total
                            </CollectSectionSubtitle>
                        </Box>
                    </CollectSectionHeader>

                    {/* Filters */}
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                        sx={{ mb: 2, flexWrap: "wrap" }}
                    >
                        <TextField
                            select
                            label="Status"
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value);
                                setPage(1);
                            }}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            {STATUS_FILTERS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Category"
                            value={category}
                            onChange={(e) => {
                                setCategory(e.target.value);
                                setPage(1);
                            }}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">All categories</MenuItem>
                            {SUPPORT_CATEGORY_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Priority"
                            value={priority}
                            onChange={(e) => {
                                setPriority(e.target.value);
                                setPage(1);
                            }}
                            size="small"
                            sx={{ minWidth: 140 }}
                        >
                            <MenuItem value="">All priorities</MenuItem>
                            {SUPPORT_PRIORITY_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Support tier"
                            value={tier}
                            onChange={(e) => {
                                setTier(e.target.value);
                                setPage(1);
                            }}
                            size="small"
                            sx={{ minWidth: 140 }}
                        >
                            {TIER_FILTERS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Search"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setSearch(searchInput.trim());
                                    setPage(1);
                                }
                            }}
                            size="small"
                            placeholder="Ticket #, subject, email"
                            InputProps={{
                                startAdornment: (
                                    <SearchOutlined
                                        sx={{ fontSize: 18, mr: 0.5, color: "#9ca3af" }}
                                    />
                                ),
                            }}
                            sx={{ minWidth: 220 }}
                        />
                        <CollectPrimaryButton
                            startIcon={<RefreshOutlined />}
                            onClick={() => {
                                setSearch(searchInput.trim());
                                setPage(1);
                                load();
                            }}
                        >
                            Apply
                        </CollectPrimaryButton>
                    </Stack>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {isLoading ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : adminTickets.length === 0 ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>
                                No tickets in the queue
                            </CollectEmptyTitle>
                            <Typography sx={{ fontSize: "0.85rem" }}>
                                Nothing matches your current filters.
                            </Typography>
                        </CollectEmptyState>
                    ) : (
                        <>
                            <CollectTableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <CollectTableHeadCell>
                                                Ticket
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Business / Tier
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Support priority
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Subject
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Status
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Submitter
                                            </CollectTableHeadCell>
                                            <CollectTableHeadCell>
                                                Created
                                            </CollectTableHeadCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {adminTickets.map(
                                            (t: IAdminSupportTicketRow) => (
                                                <TableRow
                                                    key={t._id}
                                                    hover
                                                    sx={{ cursor: "pointer" }}
                                                    onClick={() =>
                                                        navigate(
                                                            `/collect/admin/support/${t._id}`,
                                                        )
                                                    }
                                                >
                                                    <CollectTableBodyCell>
                                                        <strong>
                                                            {t.ticketNumber}
                                                        </strong>
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell>
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    "0.85rem",
                                                                fontWeight: 600,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {t.businessName}
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={
                                                                t
                                                                    .liveSubscription
                                                                    .tierLabel
                                                            }
                                                            sx={subscriptionTierChipSx(
                                                                t
                                                                    .liveSubscription
                                                                    .tierLabel,
                                                            )}
                                                        />
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell>
                                                        <Chip
                                                            size="small"
                                                            label={
                                                                t
                                                                    .liveSubscription
                                                                    .supportPriority
                                                            }
                                                            sx={supportTierChipSx(
                                                                t
                                                                    .liveSubscription
                                                                    .supportPriority,
                                                            )}
                                                        />
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell>
                                                        {t.subject}
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell>
                                                        <Chip
                                                            size="small"
                                                            label={supportStatusLabel(
                                                                t.status as SupportTicketStatus,
                                                            )}
                                                            sx={supportStatusChipSx(
                                                                t.status as SupportTicketStatus,
                                                            )}
                                                        />
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell>
                                                        {t.submitterEmail}
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell>
                                                        {formatDateTime(
                                                            t.createdAt,
                                                        )}
                                                    </CollectTableBodyCell>
                                                </TableRow>
                                            ),
                                        )}
                                    </TableBody>
                                </Table>
                            </CollectTableContainer>

                            {adminPagination.totalPages > 1 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        mt: 3,
                                    }}
                                >
                                    <Pagination
                                        count={adminPagination.totalPages}
                                        page={page}
                                        onChange={(_e, p) => setPage(p)}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </CollectSectionCard>
            </CollectContentArea>
        </CollectPageWrapper>
    );
};

export default CollectSupportQueue;
